import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '../../utils/cn'
import { Button } from '../ui/Button'
import { StatusIndicator } from '../ui/Badge'
import { useConnectionStatus, useNodes } from '../../hooks/useNodes'
import { useAuth } from '../../contexts/AuthContext'
import type { NodeInfo } from '../../types'
import { 
  Bell,
  Search, 
  Settings,
  Moon,
  Sun,
  Wifi,
  RefreshCw,
  LogOut,
  } from 'lucide-react';

interface HeaderProps {
  sidebarCollapsed: boolean
  onMobileMenuToggle?: () => void
  onMobileSearchOpen?: () => void
}

export const Header: React.FC<HeaderProps> = ({ sidebarCollapsed, onMobileMenuToggle, onMobileSearchOpen }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(true);
  const { data: connectionStatus } = useConnectionStatus();
  const { data: nodes, isLoading: nodesLoading, isError: nodesError } = useNodes();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Fleet health, DERIVED from the node list `useNodes()` fetches via
   * `apiClient.getNodes()`. Returns null -- so the readout renders nothing --
   * while the query is in flight, after it fails, and when the fleet is empty.
   * An absent measurement must never be presented as a healthy one
   * (.claude/rules/no-fabricated-values.md; this console is a SYSTEM_OPERATOR
   * surface). The prior markup hardcoded an always-online indicator under a
   * fixed healthy-system caption that read no source at all.
   */
  const fleetHealth = useMemo(() => {
    if (nodesLoading || nodesError || !Array.isArray(nodes) || nodes.length === 0) {
      return null;
    }
    const total = nodes.length;
    const healthy = nodes.filter((node) => node.status === 'healthy').length;
    const status: NodeInfo['status'] =
      healthy === total ? 'healthy' : healthy === 0 ? 'offline' : 'degraded';
    return { total, healthy, status };
  }, [nodes, nodesLoading, nodesError]);

  const handleRefresh = () => {
    // Refetches every active console query, so whichever page the operator is
    // on reloads its data. The control previously carried no onClick.
    void queryClient.invalidateQueries();
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement search functionality
    console.log('Search:', searchQuery);
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
    // Implement theme switching
    document.documentElement.classList.toggle('dark');
  };

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-30 h-16 navbar-glass transition-all duration-300',
        'left-0 lg:left-64',
        sidebarCollapsed && 'lg:left-16',
      )}
    >
      <div className="flex items-center justify-between h-full px-2 sm:px-4">
        {/* Left section - Logo on Mobile / Desktop Search */}
        <div className="flex items-center gap-4 flex-1 max-w-md pl-2 sm:pl-12 lg:pl-0">
          {/* Mobile Logo */}
          <div className="flex items-center gap-2.5 lg:hidden">
            <div className="relative w-9 h-9 bg-gradient-to-br from-accent-blue to-accent-blue-light rounded-xl flex items-center justify-center shadow-lg shadow-accent-blue/20">
              <svg className="w-6 h-6 text-white drop-shadow-sm" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 18H6c-2.21 0-4-1.79-4-4s1.79-4 4-4c.05 0 .09 0 .14.01A5.002 5.002 0 0111 5c1.84 0 3.44.99 4.32 2.47.43-.09.87-.14 1.32-.14 2.76 0 5 2.24 5 5 0 2.49-1.83 4.55-4.21 4.92-.14.95-.59 1.81-1.26 2.48-.86.86-2.02 1.39-3.26 1.39z"/>
              </svg>
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-xl pointer-events-none"></div>
            </div>
            <span className="text-lg font-bold text-white tracking-tight">SocketCloud</span>
          </div>

          {/* Desktop Search (hidden on mobile) */}
          <form onSubmit={handleSearch} className="relative flex-1 hidden lg:flex">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-console-muted pointer-events-none z-10" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input w-full text-sm text-console-text"
              style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem', color: '#e2e8f0' }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-console-muted hover:text-console-text text-lg leading-none z-10"
              >
                ×
              </button>
            )}
          </form>
        </div>

        {/* Center section - Status indicators (hidden on small screens) */}
        <div className="header-status flex items-center gap-3 sm:gap-6">
          {/* Connection Status */}
          <div className="flex items-center gap-2">
            {connectionStatus?.connected ? (
              <div className="flex items-center gap-2 text-status-online">
                <Wifi className="w-4 h-4" />
                <span className="text-xs suse-medium hidden sm:inline">Connected</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-console-muted" title="Connecting to API...">
                <Wifi className="w-4 h-4" />
                <span className="text-xs suse-medium hidden sm:inline">Connecting</span>
              </div>
            )}
          </div>

          {/* Fleet health -- measured, or absent. Bound to the node list
              `useNodes()` returns; renders nothing while loading, on error, and
              on an empty fleet (see the fleetHealth memo above).

              No Active Alerts count is rendered here. The prior markup showed a
              hardcoded Badge reading 3, which no source produced. The console
              does fetch unacknowledged alerts, but only as a page-local
              `useQuery` inside Dashboard.tsx (QUERY_KEYS.allAlerts) with its own
              response transform -- there is no alerts hook for a layout-level
              component to read, and registering a second queryFn against that
              same key from the header would hand Dashboard.tsx whichever shape
              mounted first. The count is therefore removed rather than invented;
              the Alerts route remains reachable from the bell control below. */}
          {fleetHealth && (
            <div className="hidden md:flex items-center gap-2">
              <StatusIndicator status={fleetHealth.status} showLabel={false} />
              <span className="text-xs text-console-muted">
                {fleetHealth.healthy} of {fleetHealth.total} nodes healthy
              </span>
            </div>
          )}
        </div>

        {/* Right section - Actions */}
        <div className="header-actions flex items-center gap-1 sm:gap-3">
          {/* Mobile Search Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onMobileSearchOpen}
            title="Search"
            className="touch-target-icon lg:hidden"
          >
            <Search className="w-4 h-4" />
          </Button>

          {/* Refresh -- invalidates the react-query cache the current page reads. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            title="Refresh data"
            className="touch-target-icon hidden sm:inline-flex"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            title="Toggle theme"
            className="touch-target-icon hidden sm:inline-flex"
          >
            {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          {/* Alerts -- the console's only notification surface is the Alerts
              page (App.tsx route 'alerts'). No unread indicator is rendered:
              nothing measures unread state, and a dot that is always on is a
              fabricated operator signal (no-fabricated-values.md). */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/alerts')}
            title="Alerts"
            className="touch-target-icon"
          >
            <Bell className="w-4 h-4" />
          </Button>

          {/* Configuration -- App.tsx route 'config', the page the sidebar
              labels Configuration. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/config')}
            title="Configuration"
            className="touch-target-icon hidden sm:inline-flex"
          >
            <Settings className="w-4 h-4" />
          </Button>

          {/* User Menu (simplified on mobile) */}
          <div className="header-user flex items-center gap-2 sm:gap-3 pl-2 sm:pl-3 border-l border-console-border">
            <div className="text-right hidden sm:block">
              <div className="text-sm suse-medium text-console-text">{user?.name || 'Unknown User'}</div>
              <div className="text-xs suse-light text-console-muted">{user?.email || 'Operator'}</div>
            </div>
            {/* No User Profile control: App.tsx declares no operator profile
                or account route. 'tenant/users/:id' is tenant user
                administration, not the signed-in operator's own profile, and
                socket-cloud carries no tenants (operator ruling 2026-08-24).
                The identity block above and the Logout control below are what
                remain. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="touch-target-icon text-red-400 hover:text-red-300"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Quick stats bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 opacity-60" />
    </header>
  );
};